import { InventoryStatus, ReserveStatus } from '../../types';
import getPhoneReserveButtonType from './getPhoneReserveButtonType';
import {
  getMatchedOffersByColorAndSize,
  getOffersWithInventory,
} from './offerUtils';
import { AvailableOffer, ButtonType, InventoryItem } from './types';

const colorSizeGroupingDelimiter = '::';

export const parseColorSizeStr = (str: string) => {
  const index = str.lastIndexOf(colorSizeGroupingDelimiter);
  return {
    phoneColor: str.slice(0, index),
    storageSize: str.slice(index + colorSizeGroupingDelimiter.length),
  };
};

// reduce all phone offers + the inventory response for all phone skus
// to a dictionary of color+size combinations of inventory
export const groupInventoryResponsesByColorAndSize = ({
  offers,
  inventoryItems,
}: {
  offers: AvailableOffer[];
  inventoryItems: InventoryItem[];
}): Record<string, InventoryItem[]> => offers.reduce((acc, { phoneColour, memory, sku }) => {
  const key = `${phoneColour.displayName}${colorSizeGroupingDelimiter}${memory}`;
  return {
    ...acc,
    [key]: (acc[key] ?? []).concat(
      inventoryItems.find(({ productSku }) => productSku === sku) ?? [],
    ),
  };
}, {});

type GroupedPhoneDetails = {
  matchedOffers: AvailableOffer[];
  offersWithInventory: Array<AvailableOffer & { status?: InventoryStatus }>;
  reservableCarrierExists: boolean;
};

const reservableStatus = [InventoryStatus.InStock, InventoryStatus.ShipToStore];

// reduce all phone offers plus inventory items grouped by color and size
// to a dictionary of color+size combinations and derived data for phone details + button type
export const groupPhoneDetailsByColorAndSize = ({
  offers,
  groupedInventoryItems,
  reserveStatus,
}: {
  offers: AvailableOffer[];
  groupedInventoryItems: Record<string, InventoryItem[]>;
  reserveStatus: ReserveStatus;
}): Record<string, GroupedPhoneDetails> => Object.entries(groupedInventoryItems).reduce(
  (acc: Record<string, GroupedPhoneDetails>, [combo, inventoryItems]) => {
    const matchedOffers = getMatchedOffersByColorAndSize({
      ...parseColorSizeStr(combo),
      availableOffers: offers,
    });
    const reservableCarrierExists = inventoryItems.some(
      ({ status, productSku }) => reservableStatus.includes(status)
          && matchedOffers.some(({ sku }) => sku === productSku),
    );
    return {
      ...acc,
      [combo]: {
        matchedOffers: getMatchedOffersByColorAndSize({
          ...parseColorSizeStr(combo),
          availableOffers: offers,
        }),
        reservableCarrierExists,
        offersWithInventory: getOffersWithInventory({
          matchedOffers,
          inventoryItems,
          phoneReserveStatus: reserveStatus,
          reservableCarrierExists,
        }),
      },
    };
  },
  {},
);

export const getPhoneButtonTypeFromDetails = ({
  isFFEnabledReserveAccessories,
  reserveStatus,
  reservableCarrierExists,
  noCarriers,
  loading,
}: {
  isFFEnabledReserveAccessories: boolean;
  reserveStatus: ReserveStatus;
  reservableCarrierExists: boolean;
  noCarriers: boolean;
  loading: boolean;
  deprecatedFlags: {
    disablePhoneReservation?: boolean;
    enableReserveButton?: boolean;
  };
}) => {
  if (!isFFEnabledReserveAccessories) {
    return ButtonType.Chat;
  }

  if (loading) {
    return ButtonType.None;
  }
  return (
    getPhoneReserveButtonType({
      reserveStatus,
      noCarriers,
      reservableCarrierExists,
    }) ?? ButtonType.None
  );
};

export const getGroupedButtonTypes = ({
  groupedDetails,
  ...rest
}: {
  groupedDetails: Record<string, GroupedPhoneDetails>;
  isFFEnabledReserveAccessories: boolean;
  reserveStatus: ReserveStatus;
  loading: boolean;
  deprecatedFlags: {
    disablePhoneReservation?: boolean;
    enableReserveButton?: boolean;
  };
}): Record<string, ButtonType> => Object.entries(groupedDetails).reduce((acc, [combo, details]) => {
  const { offersWithInventory, reservableCarrierExists } = details;
  return {
    ...acc,
    [combo]: getPhoneButtonTypeFromDetails({
      ...rest,
      reservableCarrierExists,
      noCarriers: offersWithInventory.length === 0,
    }),
  };
}, {});
